import { useState } from 'react';
import axios from 'axios';
import C from '../../constants/palette';

export default function AdminLogin({ onLogin, onBack }) {
  const [mode, setMode]       = useState('login');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode]       = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [message, setMessage] = useState('');

  const resetFeedback = () => {
    setErr('');
    setMessage('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      const res = await axios.post('/api/admin/auth/login', { email, password });
      onLogin(res.data);
    } catch (error) {
      setErr(error.response?.data?.error || 'Invalid credentials. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      await axios.post('/api/admin/auth/forgot-password', { email });
      setMessage('A reset code has been sent to the admin email.');
      setMode('reset');
    } catch (error) {
      setErr(error.response?.data?.error || 'Unable to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    resetFeedback();
    setLoading(true);
    try {
      await axios.post('/api/admin/auth/reset-password', { email, code, password: newPassword });
      setPassword('');
      setCode('');
      setNewPassword('');
      setMessage('Password updated. Sign in with the new password.');
      setMode('login');
    } catch (error) {
      setErr(error.response?.data?.error || 'Unable to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '13px 16px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.85)',
    fontSize: 14, outline: 'none',
    fontFamily: 'DM Mono',
  };

  const labelStyle = {
    fontSize: 9,
    letterSpacing: '0.2em',
    color: 'rgba(255,255,255,0.35)',
    fontWeight: 600,
    display: 'block',
    marginBottom: 10,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0F172A',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.06, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="900" height="900" viewBox="0 0 900 900">
          <defs>
            <radialGradient id="bgGem" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="1" />
              <stop offset="100%" stopColor="#0A192F" stopOpacity="0" />
            </radialGradient>
          </defs>
          <polygon points="450,50 750,220 870,520 750,780 450,900 150,780 30,520 150,220" fill="url(#bgGem)" />
        </svg>
      </div>

      <div style={{
        width: 440, position: 'relative',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(20px)',
        padding: '52px 48px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 42 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontFamily: 'Playfair Display', fontSize: 18, color: '#fff', letterSpacing: '0.06em' }}>
              KINGSTRADE
            </span>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            border: '1px solid rgba(255,100,100,0.3)',
            background: 'rgba(255,100,100,0.06)',
            padding: '4px 14px', borderRadius: 2,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff6464', display: 'inline-block' }} />
            <span style={{ fontSize: 9, color: 'rgba(255,100,100,0.8)', letterSpacing: '0.2em', fontWeight: 600 }}>
              RESTRICTED ACCESS
            </span>
          </div>
        </div>

        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>ADMIN EMAIL</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@kingstrade.org" style={inputStyle} required />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label style={labelStyle}>PASSWORD</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  style={{ ...inputStyle, paddingRight: 64 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  style={{
                    position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'rgba(255,255,255,0.35)', fontSize: 9, letterSpacing: '0.14em', fontWeight: 600,
                  }}
                >
                  {showPw ? 'HIDE' : 'SHOW'}
                </button>
              </div>
            </div>
            <button type="button" onClick={() => { resetFeedback(); setMode('forgot'); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 10, letterSpacing: '0.1em', padding: 0, marginBottom: 24 }}>
              FORGOT PASSWORD?
            </button>
            {err && <Feedback kind="error">{err}</Feedback>}
            {message && <Feedback>{message}</Feedback>}
            <SubmitButton loading={loading}>{loading ? 'AUTHENTICATING...' : 'ACCESS CONTROL CENTER'}</SubmitButton>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot}>
            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>ADMIN EMAIL</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@kingstrade.org" style={inputStyle} required />
            </div>
            {err && <Feedback kind="error">{err}</Feedback>}
            {message && <Feedback>{message}</Feedback>}
            <SubmitButton loading={loading}>{loading ? 'SENDING...' : 'SEND RESET CODE'}</SubmitButton>
            <SecondaryButton onClick={() => { resetFeedback(); setMode('login'); }}>BACK TO LOGIN</SecondaryButton>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={handleReset}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>RESET CODE</label>
              <input value={code} onChange={e => setCode(e.target.value)} placeholder="4-digit code" style={inputStyle} required />
            </div>
            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>NEW PASSWORD</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 8 characters" style={inputStyle} required />
            </div>
            {err && <Feedback kind="error">{err}</Feedback>}
            {message && <Feedback>{message}</Feedback>}
            <SubmitButton loading={loading}>{loading ? 'UPDATING...' : 'UPDATE PASSWORD'}</SubmitButton>
            <SecondaryButton onClick={() => { resetFeedback(); setMode('login'); }}>BACK TO LOGIN</SecondaryButton>
          </form>
        )}

        <button onClick={onBack} style={{
          display: 'block', width: '100%', marginTop: 20, padding: '10px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em',
        }}>
          Back to storefront
        </button>
      </div>
    </div>
  );
}

function Feedback({ kind = 'success', children }) {
  const isError = kind === 'error';
  return (
    <div style={{
      padding: '10px 14px',
      background: isError ? 'rgba(255,80,80,0.1)' : 'rgba(80,200,140,0.1)',
      border: `1px solid ${isError ? 'rgba(255,80,80,0.2)' : 'rgba(80,200,140,0.2)'}`,
      color: isError ? 'rgba(255,120,120,0.9)' : 'rgba(150,240,190,0.9)',
      fontSize: 11,
      marginBottom: 20,
      letterSpacing: '0.06em',
    }}>
      {children}
    </div>
  );
}

function SubmitButton({ loading, children }) {
  return (
    <button type="submit" disabled={loading} style={{
      width: '100%', padding: '14px',
      background: loading ? '#b89520' : C.gold,
      border: 'none', cursor: loading ? 'wait' : 'pointer',
      fontSize: 11, letterSpacing: '0.2em', fontWeight: 600, color: '#0F172A',
    }}>
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      width: '100%', marginTop: 12, padding: '12px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      cursor: 'pointer',
      fontSize: 10,
      color: 'rgba(255,255,255,0.45)',
      letterSpacing: '0.14em',
    }}>
      {children}
    </button>
  );
}
